import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import { path } from "../data/base";
import { describeCampaignTemplatesPayload } from "../data/campaign-template-payload";
import type { CampaignTemplate } from "../data/cockpit-data";
import { fetchConfig, useConfig, useObjectives } from "../data/queries";
import {
  TEMPLATE_BIDDING_STRATEGY_LABELS,
  TEMPLATE_BIDDING_STRATEGY_OPTIONS,
  TEMPLATE_CAMPAIGN_TYPE_OPTIONS,
  TEMPLATE_MATCH_TYPE_OPTIONS,
  TEMPLATE_PLACEMENT_OPTIONS,
  siObjectiveOptions,
} from "../data/tactic-catalogue";
import { QueryErrorState, TabSkeleton } from "./tab-states";

const INPUT_CLASS =
  "min-h-10 w-full rounded-ks-sm border border-ks-grey-3 bg-ks-surface px-3 text-sm text-ks-ink disabled:bg-muted";

type EditorState =
  | { open: false }
  | { open: true; mode: "create" | "edit" | "duplicate"; originalName: string | null };

const EMPTY_TEMPLATE: CampaignTemplate = {
  name: "",
  campaign_type: "SP",
  funnel_stage: "CONVERSION",
  match_type: "EXACT",
  placement: "ToS",
  placement_percentage: 0,
  bidding_strategy: "LEGACY_FOR_SALES",
  default_budget: 25,
  si_objective: "",
};

/**
 * The reusable campaign shapes a create-campaign tactic can choose.
 *
 * Two things make this panel more than a CRUD list:
 *
 * * **The whole catalogue is written at once, under an `expected_updated_at`
 *   check.** Two people editing different templates would otherwise silently
 *   overwrite each other, because the write is the array. A 409 is surfaced as
 *   "someone else changed this — reload and reapply", which is what happened,
 *   rather than as a failure.
 * * **A template is retired, never deleted.** Campaigns created from it still
 *   reference it, and a name that vanishes takes their provenance with it.
 */
export function CampaignTemplatePanel({ onSaved }: { onSaved: (message: string) => void }) {
  const config = useConfig();
  const objectives = useObjectives();
  const queryClient = useQueryClient();
  const catalogue = config.data?.campaign_template_catalogue;
  const [editor, setEditor] = useState<EditorState>({ open: false });
  const [form, setForm] = useState<CampaignTemplate>(EMPTY_TEMPLATE);
  const [draftCatalogue, setDraftCatalogue] = useState<CampaignTemplate[] | null>(null);
  const [expectedUpdatedAt, setExpectedUpdatedAt] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [stale, setStale] = useState(false);
  const [saving, setSaving] = useState(false);

  const funnelStageOptions = useMemo(
    () => distinctSorted(catalogue?.map((template) => template.funnel_stage) ?? []),
    [catalogue],
  );
  const siOptions = useMemo(
    () =>
      siObjectiveOptions(
        objectives.data?.summary.map((row) => row.objective) ?? [],
        form.si_objective || null,
      ),
    [form.si_objective, objectives.data?.summary],
  );

  useEffect(() => {
    if (!editor.open && catalogue) {
      setDraftCatalogue(null);
      setExpectedUpdatedAt(config.data?.campaign_template_meta?.updated_at ?? null);
    }
  }, [catalogue, config.data?.campaign_template_meta?.updated_at, editor.open]);

  function clearError() {
    setError("");
    setStale(false);
  }

  function openCreate() {
    setForm({ ...EMPTY_TEMPLATE });
    setEditor({ open: true, mode: "create", originalName: null });
    setExpectedUpdatedAt(config.data?.campaign_template_meta?.updated_at ?? null);
    clearError();
  }

  function openEdit(template: CampaignTemplate) {
    setForm({ ...template });
    setEditor({ open: true, mode: "edit", originalName: template.name });
    setExpectedUpdatedAt(config.data?.campaign_template_meta?.updated_at ?? null);
    clearError();
  }

  function openDuplicate(template: CampaignTemplate) {
    setForm({ ...template, name: `${template.name}-COPY`, retired: undefined });
    setEditor({ open: true, mode: "duplicate", originalName: template.name });
    setExpectedUpdatedAt(config.data?.campaign_template_meta?.updated_at ?? null);
    clearError();
  }

  function candidateWith(nextTemplate: CampaignTemplate): CampaignTemplate[] {
    const base = draftCatalogue ?? catalogue ?? [];
    if (!editor.open || editor.mode === "create" || editor.mode === "duplicate") {
      return [...base, nextTemplate];
    }
    return base.map((template) => (template.name === editor.originalName ? nextTemplate : template));
  }

  async function postCatalogue(next: CampaignTemplate[]) {
    const requestBody = { value: next, reason: null, expected_updated_at: expectedUpdatedAt };
    // Validated here, before the network: the payload rules are the data layer's,
    // so an invalid catalogue is named in the operator's words rather than
    // bounced back as an opaque 400.
    const violation = describeCampaignTemplatesPayload(requestBody);
    if (violation) {
      setError(violation);
      return false;
    }
    setDraftCatalogue(next);
    setSaving(true);
    clearError();
    try {
      const response = await fetch(path("/campaign-templates"), {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(requestBody),
      });
      const result = (await response.json().catch(() => ({}))) as {
        error?: string;
        detail?: string;
        count?: number;
      };
      if (!response.ok) {
        setError([result.error, result.detail].filter(Boolean).join(": ") || `HTTP ${response.status}`);
        setStale(response.status === 409 && result.error === "stale_campaign_templates");
        return false;
      }
      // Refetched FRESH rather than merely invalidated: the config read carries a
      // 60s cache-control, so a plain invalidate returns the pre-write catalogue.
      await queryClient.invalidateQueries({ queryKey: ["machine", "config"], refetchType: "none" });
      await queryClient.fetchQuery({
        queryKey: ["machine", "config"],
        queryFn: () => fetchConfig({ fresh: true }),
      });
      setDraftCatalogue(null);
      setEditor({ open: false });
      onSaved(`Saved. ${result.count ?? next.length} templates.`);
      return true;
    } catch {
      setError("network_error: The campaign templates could not be saved.");
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function saveForm() {
    await postCatalogue(candidateWith(form));
  }

  async function setRetired(template: CampaignTemplate, retired: boolean) {
    const base = draftCatalogue ?? catalogue ?? [];
    await postCatalogue(
      base.map((item) =>
        item.name === template.name ? { ...item, ...(retired ? { retired: true } : { retired: undefined }) } : item,
      ),
    );
  }

  async function reloadAfterConflict() {
    await config.refetch();
    setDraftCatalogue(null);
    setEditor({ open: false });
    clearError();
  }

  const shownCatalogue = draftCatalogue ?? catalogue ?? [];

  return (
    <Accordion type="single" collapsible defaultValue="campaign-templates">
      <AccordionItem value="campaign-templates">
        <AccordionTrigger>Campaign templates</AccordionTrigger>
        <AccordionContent>
          <p className="mb-4 max-w-prose text-sm text-muted-foreground">
            The campaign shapes a create-campaign tactic can choose. Market, ASIN, target group and keyword are
            bound when the tactic is applied, not here.
          </p>
          {config.isPending ? (
            <TabSkeleton rows={2} />
          ) : config.isError || !catalogue || catalogue.length === 0 ? (
            <QueryErrorState title="Campaign templates unavailable" onRetry={() => void config.refetch()} />
          ) : editor.open ? (
            <TemplateForm
              template={form}
              mode={editor.mode}
              error={error}
              stale={stale}
              saving={saving}
              funnelStageOptions={funnelStageOptions}
              siObjectiveOptions={siOptions}
              siObjectivesLoaded={objectives.isSuccess && !objectives.isError}
              onChange={setForm}
              onCancel={() => {
                setEditor({ open: false });
                clearError();
              }}
              onReload={() => void reloadAfterConflict()}
              onSave={() => void saveForm()}
            />
          ) : (
            <div className="flex flex-col gap-4">
              {error && <SaveError error={error} stale={stale} onReload={() => void reloadAfterConflict()} />}
              <div className="flex justify-end">
                <Button onClick={openCreate}>Create template</Button>
              </div>
              <div className="flex flex-col gap-3">
                {shownCatalogue.map((template) => (
                  <TemplateRow
                    key={template.name}
                    template={template}
                    saving={saving}
                    onEdit={() => openEdit(template)}
                    onDuplicate={() => openDuplicate(template)}
                    onRetired={(retired) => void setRetired(template, retired)}
                  />
                ))}
              </div>
            </div>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

function TemplateRow({
  template,
  saving,
  onEdit,
  onDuplicate,
  onRetired,
}: {
  template: CampaignTemplate;
  saving: boolean;
  onEdit: () => void;
  onDuplicate: () => void;
  onRetired: (retired: boolean) => void;
}) {
  return (
    <Card className={template.retired ? "opacity-70" : undefined}>
      <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm font-semibold text-ks-ink">{template.name}</span>
            <Badge variant="outline">{template.retired ? "Retired" : "From build"}</Badge>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            {template.placement} · {template.match_type} · bid {template.placement_percentage}% · budget{" "}
            {template.default_budget.toFixed(2)}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">{template.si_objective}</p>
        </div>
        <div className="flex flex-none flex-wrap gap-2">
          <Button variant="outline" size="lg" disabled={saving} onClick={onEdit}>
            Edit
          </Button>
          <Button variant="outline" size="lg" disabled={saving} onClick={onDuplicate}>
            Duplicate
          </Button>
          <Button variant="outline" size="lg" disabled={saving} onClick={() => onRetired(!template.retired)}>
            {template.retired ? "Restore" : "Retire"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function TemplateForm({
  template,
  mode,
  error,
  stale,
  saving,
  funnelStageOptions,
  siObjectiveOptions: siOptions,
  siObjectivesLoaded,
  onChange,
  onCancel,
  onReload,
  onSave,
}: {
  template: CampaignTemplate;
  mode: "create" | "edit" | "duplicate";
  error: string;
  stale: boolean;
  saving: boolean;
  funnelStageOptions: readonly string[];
  siObjectiveOptions: readonly { value: string; label: string; notInSi?: boolean }[];
  siObjectivesLoaded: boolean;
  onChange: (template: CampaignTemplate) => void;
  onCancel: () => void;
  onReload: () => void;
  onSave: () => void;
}) {
  const set = <K extends keyof CampaignTemplate>(key: K, value: CampaignTemplate[K]) =>
    onChange({ ...template, [key]: value });

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h4 className="text-base font-semibold text-ks-ink">
          {mode === "edit" ? `Edit ${template.name}` : mode === "duplicate" ? "Duplicate template" : "Create template"}
        </h4>
        <p className="mt-1 text-sm text-muted-foreground">
          Define the reusable campaign shape. Allocation values are supplied later.
        </p>
      </div>
      {error && <SaveError error={error} stale={stale} onReload={onReload} />}
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Name">
          <Input
            required
            maxLength={64}
            disabled={mode === "edit"}
            className="min-h-10"
            value={template.name}
            onChange={(event) => set("name", event.target.value)}
          />
          {mode === "edit" && (
            <Help>
              A template name is its identity. To rename, duplicate under the new name and retire this one.
            </Help>
          )}
        </Field>
        <Field label="Description">
          <Input
            maxLength={280}
            className="min-h-10"
            value={template.description ?? ""}
            onChange={(event) => set("description", event.target.value)}
          />
        </Field>
        <SelectField
          label="Campaign type"
          value={template.campaign_type}
          options={TEMPLATE_CAMPAIGN_TYPE_OPTIONS}
          onChange={(value) => set("campaign_type", value)}
        />
        <CatalogueValueField
          label="Funnel stage"
          value={template.funnel_stage}
          options={funnelStageOptions}
          onChange={(value) => set("funnel_stage", value)}
          onBlur={() => set("funnel_stage", template.funnel_stage.toUpperCase())}
        />
        <SelectField
          label="Match type"
          value={template.match_type}
          options={TEMPLATE_MATCH_TYPE_OPTIONS}
          onChange={(value) => set("match_type", value)}
        />
        <SelectField
          label="Placement"
          value={template.placement}
          options={TEMPLATE_PLACEMENT_OPTIONS}
          onChange={(value) => set("placement", value)}
        />
        <Field label="Placement bid multiplier">
          <Input
            required
            type="number"
            min={0}
            max={900}
            step={1}
            className="min-h-10"
            value={template.placement_percentage}
            onChange={(event) => set("placement_percentage", Number(event.target.value))}
          />
        </Field>
        <SelectField
          label="Bidding strategy"
          value={template.bidding_strategy}
          options={TEMPLATE_BIDDING_STRATEGY_OPTIONS}
          labels={TEMPLATE_BIDDING_STRATEGY_LABELS}
          onChange={(value) => set("bidding_strategy", value)}
        />
        <Field label="Default daily budget">
          <Input
            required
            type="number"
            min={0.01}
            max={500}
            step={0.01}
            className="min-h-10"
            value={template.default_budget}
            onChange={(event) => set("default_budget", Number(event.target.value))}
          />
          <Help>A ceiling for campaigns created from this template. Spend-affecting.</Help>
        </Field>
        <SiObjectiveField
          value={template.si_objective}
          options={siOptions}
          loaded={siObjectivesLoaded}
          onChange={(value) => set("si_objective", value)}
        />
        {mode !== "create" && (
          <label className="flex items-center gap-2 text-[13px] font-medium text-muted-foreground">
            <input
              type="checkbox"
              checked={template.retired === true}
              onChange={(event) => set("retired", event.target.checked || undefined)}
            />
            Retired
          </label>
        )}
      </div>
      <div className="flex justify-end gap-3 border-t border-ks-grey-3 pt-5">
        <Button variant="outline" size="lg" onClick={onCancel}>
          Cancel
        </Button>
        {/* Save stays disabled until the SI objective list has actually loaded:
            saving with an unloaded list would write an empty objective, which
            upstream accepts and which silently mis-targets every campaign made
            from the template. */}
        <Button
          size="lg"
          disabled={saving || !siObjectivesLoaded || !template.si_objective.trim()}
          onClick={onSave}
        >
          {saving ? "Saving…" : "Save template"}
        </Button>
      </div>
    </div>
  );
}

function SaveError({ error, stale, onReload }: { error: string; stale: boolean; onReload: () => void }) {
  return (
    <div role="alert" className="flex flex-col gap-2 text-sm font-medium text-ks-danger">
      <p>{error}</p>
      {stale && (
        <>
          <p>
            Someone else changed the templates while you were editing. Reload to see the current list, then
            reapply your change.
          </p>
          <Button variant="outline" size="sm" className="self-start" onClick={onReload}>
            Reload
          </Button>
        </>
      )}
    </div>
  );
}

function SelectField({
  label,
  value,
  options,
  labels,
  onChange,
}: {
  label: string;
  value: string;
  options: readonly string[];
  labels?: Readonly<Record<string, string>>;
  onChange: (value: string) => void;
}) {
  return (
    <Field label={label}>
      <select className={INPUT_CLASS} value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => (
          <option key={option} value={option}>
            {labels?.[option] ?? option}
          </option>
        ))}
      </select>
    </Field>
  );
}

function SiObjectiveField({
  value,
  options,
  loaded,
  onChange,
}: {
  value: string;
  options: readonly { value: string; label: string; notInSi?: boolean }[];
  loaded: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <Field label="SI objective">
      <select
        required
        className={INPUT_CLASS}
        value={loaded ? value : ""}
        disabled={!loaded}
        onChange={(event) => onChange(event.target.value)}
      >
        {!loaded ? (
          <option value="">Loading SI objectives</option>
        ) : (
          <>
            <option value="" disabled>
              Select SI objective
            </option>
            {options.map((option) => (
              <option key={option.value} value={option.value} disabled={option.notInSi}>
                {option.label}
              </option>
            ))}
          </>
        )}
      </select>
    </Field>
  );
}

const CUSTOM_VALUE = "__custom__";

/** A value from the catalogue, or a new one typed in — both, without two fields. */
function CatalogueValueField({
  label,
  value,
  options,
  maxLength,
  onChange,
  onBlur,
}: {
  label: string;
  value: string;
  options: readonly string[];
  maxLength?: number;
  onChange: (value: string) => void;
  onBlur?: () => void;
}) {
  const [custom, setCustom] = useState(value === "");
  const shownOptions = options.includes(value) || value === "" ? options : [...options, value];
  return (
    <Field label={label}>
      <select
        className={INPUT_CLASS}
        value={custom ? CUSTOM_VALUE : value}
        onChange={(event) => {
          if (event.target.value === CUSTOM_VALUE) {
            setCustom(true);
          } else {
            setCustom(false);
            onChange(event.target.value);
          }
        }}
      >
        {shownOptions.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
        <option value={CUSTOM_VALUE}>Custom…</option>
      </select>
      {custom && (
        <Input
          required
          maxLength={maxLength}
          className="min-h-10"
          value={value}
          onBlur={onBlur}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </Field>
  );
}

function distinctSorted(values: readonly string[]): string[] {
  return [...new Set(values)].sort((left, right) => left.localeCompare(right));
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5 text-[13px] font-medium text-muted-foreground">
      <span>{label}</span>
      {children}
    </label>
  );
}

function Help({ children }: { children: ReactNode }) {
  return <span className="text-xs font-normal text-muted-foreground">{children}</span>;
}
